import { z } from 'zod';

/** `T | null | undefined` wrapper that matches how token-api serializes JSON columns. */
const maybe = <T extends z.ZodType>(schema: T) => schema.nullable().optional();

/**
 * Mirrors `zSecurityResultSchema` from `token-api-interface`. Source of truth
 * is the `tokens.tokens_security` table, populated from GoPlus.
 */
export const tokenSecuritySchema = z.object({
  address: z.string(),
  chainId: z.number(),
  chainName: maybe(z.string()),
  tokenName: maybe(z.string()),
  tokenSymbol: maybe(z.string()),
  tokenDescription: maybe(z.string()),
  tokenUri: maybe(z.string()),
  buyTax: maybe(z.number()),
  sellTax: maybe(z.number()),
  transferTax: maybe(z.number()),
  isInDex: maybe(z.boolean()),
  isHoneypot: maybe(z.boolean()),
  isBlacklisted: maybe(z.boolean()),
  isWhitelisted: maybe(z.boolean()),
  isAntiWhale: maybe(z.boolean()),
  isAirdropScam: maybe(z.boolean()),
  isMintable: maybe(z.boolean()),
  isProxy: maybe(z.boolean()),
  isTrueToken: maybe(z.boolean()),
  isOpenSource: maybe(z.boolean()),
  isMetadataMutable: maybe(z.boolean()),
  isFreezable: maybe(z.boolean()),
  isClosable: maybe(z.boolean()),
  isTransferFeeUpgradable: maybe(z.boolean()),
  isDefaultStateUpgradable: maybe(z.boolean()),
  isTransferHookUpgradable: maybe(z.boolean()),
  cannotBuy: maybe(z.boolean()),
  cannotSellAll: maybe(z.boolean()),
  slippageModifiable: maybe(z.boolean()),
  transferPausable: maybe(z.boolean()),
  antiWhaleModifiable: maybe(z.boolean()),
  tradingCooldown: maybe(z.boolean()),
  personalSlippageModifiable: maybe(z.boolean()),
  externalCall: maybe(z.boolean()),
  fakeToken: maybe(z.boolean()),
  hiddenOwner: maybe(z.boolean()),
  selfdestruct: maybe(z.boolean()),
  nonTransferable: maybe(z.boolean()),
  trustedToken: maybe(z.boolean()),
  trustList: maybe(z.boolean()),
  canTakeBackOwnership: maybe(z.boolean()),
  gasAbuse: maybe(z.boolean()),
  creatorAddress: maybe(z.string()),
  creatorBalance: maybe(z.number()),
  creatorPercent: maybe(z.number()),
  holderCount: maybe(z.number()),
  holders: maybe(z.unknown()),
  lpHolderCount: maybe(z.number()),
  lpHolders: maybe(z.unknown()),
  lpTotalSupply: maybe(z.number()),
  ownerAddress: maybe(z.string()),
  ownerBalance: maybe(z.number()),
  ownerChangeBalance: maybe(z.boolean()),
  ownerPercent: maybe(z.number()),
  honeypotWithSameCreator: maybe(z.number()),
  totalSupply: maybe(z.number()),
  top10HoldersPercent: maybe(z.number()),
  trueTokenAddress: maybe(z.string()),
  defaultAccountState: maybe(z.string()),
  note: maybe(z.string()),
  otherPotentialRisks: maybe(z.string()),
  dex: maybe(z.unknown()),
  mintableAuthority: maybe(z.unknown()),
  metadataMutableAuthority: maybe(z.unknown()),
  freezableAuthority: maybe(z.unknown()),
  closableAuthority: maybe(z.unknown()),
  balanceMutableAuthorityInfo: maybe(z.unknown()),
  transferFeeUpgradableAuthority: maybe(z.unknown()),
  defaultStateUpgradableAuthority: maybe(z.unknown()),
  transferHookUpgradableAuthority: maybe(z.unknown()),
  transferFee: maybe(z.unknown()),
  transferHook: maybe(z.unknown()),
  creators: maybe(z.unknown()),
});

export type TokenSecurity = z.infer<typeof tokenSecuritySchema>;

export const tokenSecurityResponseSchema = z.object({
  data: z.array(tokenSecuritySchema),
  resultCount: z.number(),
});

/** Shared response shape for matcha's `/api/tokens/security` proxy route. */
export type TokenSecurityApiResponse =
  | { data: { securityInfo: TokenSecurity } }
  | { error: string };

/** Entry in the `creators` JSON column, passed through verbatim from GoPlus. */
export interface TokenSecurityCreator {
  address: string | null;
  malicious_address: number | null;
}

/** Shape of the `transferFee` JSON column, passed through verbatim from GoPlus. */
export interface TokenSecurityTransferFee {
  current_fee_rate?: {
    fee_rate?: string;
    maximum_fee?: string;
  };
  scheduled_fee_rate?: Array<{
    epoch?: string;
    fee_rate?: string;
    maximum_fee?: string;
  }>;
}

/** Narrow the `unknown` `transferFee` column to its GoPlus-native shape. */
export const getTransferFee = (
  sec: TokenSecurity | undefined,
): TokenSecurityTransferFee | null | undefined =>
  sec?.transferFee as TokenSecurityTransferFee | null | undefined;

export const getTransferFeeRate = (sec: TokenSecurity | undefined): number =>
  Number(getTransferFee(sec)?.current_fee_rate?.fee_rate ?? 0);

export const getCreators = (
  sec: TokenSecurity | undefined,
): TokenSecurityCreator[] | null | undefined =>
  sec?.creators as TokenSecurityCreator[] | null | undefined;

export const getFirstCreator = (sec: TokenSecurity | undefined): TokenSecurityCreator | undefined =>
  getCreators(sec)?.[0] ?? undefined;

/**
 * Equivalent to GoPlus's `balance_mutable_authority.status === '1'`: the
 * authority list is only populated when the feature is present, and the
 * token-api does not surface a flat boolean for it.
 */
export const hasBalanceMutableAuthority = (sec: TokenSecurity | undefined): boolean => {
  const info = sec?.balanceMutableAuthorityInfo;
  return Array.isArray(info) && info.length > 0;
};

/** Convert a GoPlus binary flag (`1` | `0` | nullish) to a three-state boolean. */
export const binaryFlagToBool = (flag: number | null | undefined): boolean | null =>
  flag === 1 ? true : flag === 0 ? false : null;
